import React from "react";
import { ShieldStar, FileText, Buildings, MedalMilitary, Download, CheckCircle } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import WalkthroughScheduler from "@/components/WalkthroughScheduler";
import { GOV_DATA } from "@/lib/site";

export default function Government() {
  return (
    <>
      <SEO title="Government Contracting" description="SAM.gov registered facilities maintenance contractor. NAICS 561720, 561740, 561790. GSA-aligned proposals, contract-ready compliance." path="/government" />
      <PageHero
        eyebrow="Government Contracting"
        title="Contract-ready facility services for federal, state & local."
        subtitle="SAM.gov active. NAICS-coded. Audit-trail by design. We make the contracting officer's job easy."
        image="https://images.pexels.com/photos/17718824/pexels-photo-17718824.jpeg"
        testId="government-hero"
      />

      {/* Data Strip */}
      <section className="bg-white border-b border-[var(--es-border)]" data-testid="gov-data-strip">
        <div className="es-container py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "SAM.gov Status", value: GOV_DATA.sam, icon: ShieldStar },
            { label: "UEI / CAGE", value: GOV_DATA.uei, icon: FileText },
            { label: "Primary NAICS", value: "561720", icon: Buildings },
            { label: "Contract Vehicles", value: `${GOV_DATA.vehicles.length} active`, icon: MedalMilitary },
          ].map((s) => (
            <div key={s.label} className="border-l-2 border-[var(--es-accent)] pl-4">
              <s.icon size={22} className="text-[var(--es-navy)] mb-2" weight="duotone" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--es-text-muted)]">{s.label}</div>
              <div className="font-bold text-[var(--es-navy)] mt-1" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Capability Statement */}
      <section className="es-section bg-[var(--es-bg)]">
        <div className="es-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="es-eyebrow">Capability Statement</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Why contracting officers choose Empire Serv.</h2>
            <p className="es-lede mt-4">A self-performing, ISO 9001:2015 facility-services contractor with documented past performance across federal courthouses, VA outpatient clinics, and state office complexes.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {[
                "Background-cleared workforce available",
                "Davis-Bacon & Service Contract Act compliant payroll",
                "FAR-aligned subcontracting plan",
                "Bonded, insured, and E-Verify enrolled",
                "Audit-ready QASP documentation",
                "EPA Safer Choice & Green Seal default chemistries",
              ].map((c) => (
                <div key={c} className="flex items-start gap-3 text-sm">
                  <CheckCircle size={18} weight="fill" className="text-[var(--es-accent)] mt-0.5 flex-shrink-0" />
                  <span className="text-[var(--es-text)]">{c}</span>
                </div>
              ))}
            </div>
            <a href="/quote" data-testid="gov-cap-statement-cta" className="es-btn-accent mt-8 inline-flex">
              <Download size={18} weight="bold" />Request capability statement (PDF)
            </a>
          </div>

          <aside className="lg:col-span-5 bg-[var(--es-navy)] text-white p-8 rounded-sm">
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--es-accent)] font-semibold">NAICS Codes</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {GOV_DATA.naics.map((n) => (
                <li key={n} className="font-mono text-white/90 border-b border-white/10 pb-2.5">{n}</li>
              ))}
            </ul>
            <div className="mt-7 text-xs uppercase tracking-[0.25em] text-[var(--es-accent)] font-semibold">Contract Vehicles</div>
            <ul className="mt-4 space-y-2 text-sm">
              {GOV_DATA.vehicles.map((v) => (
                <li key={v} className="text-white/90">• {v}</li>
              ))}
            </ul>
            <div className="mt-7 text-xs uppercase tracking-[0.25em] text-[var(--es-accent)] font-semibold">Business Classifications</div>
            <ul className="mt-4 space-y-2 text-sm">
              {GOV_DATA.small_business.map((v) => (
                <li key={v} className="text-white/90">• {v}</li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="es-section bg-white">
        <div className="es-container">
          <div className="es-eyebrow">Past Performance Snapshot</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3 max-w-3xl">A track record contracting officers can verify.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { agency: "Federal Courthouse", scope: "Multi-floor secure facility", years: "3 option years exercised", score: "99.6% QASP avg." },
              { agency: "VA Outpatient Clinic", scope: "Medical-grade EVS support", years: "5-year base + options", score: "Zero life-safety findings" },
              { agency: "State Office Complex", scope: "740,000 sq ft term contract", years: "Active since 2021", score: "98.4% inspection avg." },
            ].map((p) => (
              <div key={p.agency} className="es-card p-7" data-testid={`gov-past-perf-${p.agency.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--es-accent)]">{p.agency}</div>
                <h3 className="mt-2 text-lg font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{p.scope}</h3>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--es-text-muted)]">Term</div>
                    <div className="font-semibold text-[var(--es-text)] mt-1">{p.years}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--es-text-muted)]">Performance</div>
                    <div className="font-semibold text-[var(--es-text)] mt-1">{p.score}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Solicitation in flight?" title="Send us your RFP — we respond fast." subtitle="Forward your RFP, SOW, or PWS. We'll return a compliant proposal with capability statement attached." />

      {/* Walkthrough scheduler */}
      <section className="es-section bg-[var(--es-bg)]" data-testid="gov-walkthrough-section">
        <div className="es-container">
          <WalkthroughScheduler variant="light" />
        </div>
      </section>
    </>
  );
}
